import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { calculateROI, calculateBCR, calculatePaybackMonths, BENEFIT_TYPE_LABELS, COST_LABELS, type ROICostStructure, type ROIBenefitItem, type ProjectROIData } from '@/data/roiModelData';
import { LIDER_IA_COMPONENTS } from '@/data/liderIAEvaluationData';
import { paginateQuery } from '@/lib/paginateSupabase';
import { useAuth } from '@/contexts/AuthContext';
import { useAuditorCohortIds } from '@/hooks/useAuditorCohortIds';

export interface PortfolioProject {
  id: string;
  name: string;
  status: string;
  submitted: boolean;
  submittedAt: string | null;
  createdAt: string;
  cohortId: string | null;
  cohortName: string | null;
  userId: string;
  company: string | null;
  area: string | null;
  participantName: string | null;
  participantEmail: string | null;
  solutionType: number | null;
  problem: string | null;
  solutionDescription: string | null;
  totalScore: number;
  classification: string | null;
  roiData: ProjectROIData | null;
  tools: string[];
  hasDeliverable: boolean;
  deliverableCount: number;
  fileUrl: string | null;
  fileUrls: string[];
  invitedParticipants: string[];
  collaborators: Array<{ userId: string; name: string; email: string }>;
  answeredCount: number;
  totalQuestions: number;
  answeredAll: boolean;
  approvedAt: string | null;
  approvedBy: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  hasEvaluation: boolean;
}

export interface PortfolioROISummary {
  projectId: string;
  projectName: string;
  company: string;
  costs: ROICostStructure;
  benefits: ROIBenefitItem[];
  totalCosts: number;
  totalBenefits: number;
  roi: number;
  bcr: number;
  paybackMonths: number;
}

export function usePortfolioProjects(organizationId?: string) {
  const { user, currentRole } = useAuth();
  const isAuditor = currentRole === 'auditor';
  // Las policies RLS se suman: si el auditor también es admin de su empresa, la
  // RLS le devuelve además los proyectos de esa empresa. En modo auditor se
  // limita a los participantes de sus cohortes asignadas.
  const auditorScope = useAuditorCohortIds();
  return useQuery({
    queryKey: ['portfolio-projects', organizationId, isAuditor ? `${auditorScope.cohortIds.join(',')}:${auditorScope.participantIds.length}` : null],
    queryFn: async () => {
      // 1. Get all projects (admin scope via RLS) — paginado para sortear
      //    el `max-rows = 1000` de PostgREST.
      const fetched = await paginateQuery<any>((from, to) =>
        supabase
          .from('participant_projects')
          .select('*')
          .order('created_at', { ascending: false })
          .range(from, to)
      );
      const auditorParticipants = new Set(auditorScope.participantIds);
      const projects = isAuditor
        ? fetched.filter((p) => auditorParticipants.has(p.user_id))
        : fetched;
      if (projects.length === 0) return [];

      const userIds = [...new Set(projects.map(p => p.user_id))];

      // Helper: chunk an array
      const chunk = <T,>(arr: T[], size: number): T[][] => {
        const out: T[][] = [];
        for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
        return out;
      };

      // 2. Parallel fetches — fetch small aux tables in full to avoid huge .in(...) URLs.
      //    Profiles is paginated by userId chunks (PII + large table).
      const profileChunks = chunk(userIds, 150);
      const [evalData, cohortRes, toolsData, collabData, ...profileResults] = await Promise.all([
        paginateQuery<any>((from, to) =>
          supabase.from('project_evaluations').select('*').range(from, to)
        ),
        supabase.from('cohorts').select('id, name, organization_id'),
        paginateQuery<{ project_id: string; tool_name: string }>((from, to) =>
          supabase.from('project_tools').select('project_id, tool_name').range(from, to)
        ),
        paginateQuery<{ project_id: string; user_id: string }>((from, to) =>
          supabase.from('project_collaborators').select('project_id, user_id').range(from, to)
        ),
        ...profileChunks.map(ids =>
          supabase
            .from('profiles')
            .select('id, full_name, company, area, email, organization_id')
            .in('id', ids)
        ),
      ]);

      if (cohortRes.error) throw cohortRes.error;
      const profilesData: any[] = [];
      for (const pr of profileResults) {
        if (pr.error) throw pr.error;
        profilesData.push(...(pr.data || []));
      }

      // Resolve collaborator names (fetch any missing profiles in one extra batch).
      const collabRows = collabData;
      const knownIds = new Set(profilesData.map((p: any) => p.id));
      const missingCollabIds = [...new Set(collabRows.map(c => c.user_id).filter(id => id && !knownIds.has(id)))];
      if (missingCollabIds.length > 0) {
        const extraChunks = chunk(missingCollabIds, 150);
        const extraResults = await Promise.all(extraChunks.map(ids =>
          supabase.from('profiles').select('id, full_name, email').in('id', ids)
        ));
        for (const r of extraResults) {
          if (r.error) throw r.error;
          profilesData.push(...(r.data || []));
        }
      }

      const evalMap = new Map(evalData.map((e: any) => [e.project_id, e]));
      const profileMap = new Map(profilesData.map(p => [p.id, p]));
      const cohortMap = new Map((cohortRes.data || []).map(c => [c.id, c]));
      const toolsMap = new Map<string, string[]>();
      toolsData.forEach(t => {
        const arr = toolsMap.get(t.project_id) || [];
        arr.push(t.tool_name);
        toolsMap.set(t.project_id, arr);
      });
      const collabMap = new Map<string, string[]>();
      const collabDetailMap = new Map<string, Array<{ userId: string; name: string; email: string }>>();
      collabRows.forEach(c => {
        const prof = profileMap.get(c.user_id);
        const name = prof?.full_name || prof?.email || '';
        if (!name) return;
        const arr = collabMap.get(c.project_id) || [];
        arr.push(name);
        collabMap.set(c.project_id, arr);

        const det = collabDetailMap.get(c.project_id) || [];
        det.push({
          userId: c.user_id,
          name: prof?.full_name || '',
          email: prof?.email || '',
        });
        collabDetailMap.set(c.project_id, det);
      });

      // 3. Build results
      let results: PortfolioProject[] = projects.map(p => {
        const ev = evalMap.get(p.id);
        const profile = profileMap.get(p.user_id);
        const cohort = p.cohort_id ? cohortMap.get(p.cohort_id) : null;
        const fileUrlsArr = Array.isArray((p as any).file_urls) ? (p as any).file_urls as any[] : [];
        const hasSingle = !!(p as any).file_url && String((p as any).file_url).trim() !== '';
        const deliverableCount = fileUrlsArr.length + (hasSingle && fileUrlsArr.length === 0 ? 1 : 0);
        const normalizedFileUrls = fileUrlsArr
          .map((f: any) => (typeof f === 'string' ? f : f?.url || ''))
          .filter((u: string) => !!u);
        const rubricAnswers = ((p as any).rubric_answers || {}) as Record<string, string>;
        const totalQuestions = LIDER_IA_COMPONENTS.length;
        const answeredCount = LIDER_IA_COMPONENTS.filter(c => ((rubricAnswers[c.id] || '').trim().length >= 20)).length;
        return {
          id: p.id,
          name: p.name,
          status: p.status || 'ideation',
          submitted: p.submitted || false,
          submittedAt: p.submitted_at,
          createdAt: p.created_at,
          cohortId: p.cohort_id,
          cohortName: cohort?.name || null,
          userId: p.user_id,
          company: profile?.company || null,
          area: profile?.area || null,
          participantName: profile?.full_name || null,
          participantEmail: profile?.email || null,
          solutionType: p.solution_type,
          problem: p.problem,
          solutionDescription: p.solution_description,
          totalScore: ev?.total_score || 0,
          classification: ev?.classification || null,
          roiData: p.roi_data as unknown as ProjectROIData | null,
          tools: toolsMap.get(p.id) || [],
          hasDeliverable: deliverableCount > 0,
          deliverableCount,
          fileUrl: (p as any).file_url || null,
          fileUrls: normalizedFileUrls,
          invitedParticipants: collabMap.get(p.id) || [],
          collaborators: collabDetailMap.get(p.id) || [],
          answeredCount,
          totalQuestions,
          answeredAll: totalQuestions > 0 && answeredCount >= totalQuestions,
          approvedAt: (p as any).approved_at || null,
          approvedBy: (p as any).approved_by || null,
          rejectedAt: (p as any).rejected_at || null,
          rejectionReason: (p as any).rejection_reason || null,
          hasEvaluation: !!ev,
        };
      });

      // 4. Filter by org if needed
      if (organizationId && organizationId !== 'all') {
        const orgCohortIds = new Set(
          (cohortRes.data || []).filter((c: any) => c.organization_id === organizationId).map((c: any) => c.id)
        );
        const orgUserIds = new Set(
          profilesData.filter((p: any) => p.organization_id === organizationId).map((p: any) => p.id)
        );
        results = results.filter(r =>
          (r.cohortId && orgCohortIds.has(r.cohortId)) || orgUserIds.has(r.userId)
        );
      }

      return results;
    },
    // Espera a que currentRole resuelva (y por lo tanto el header x-active-role
    // que las policies RLS de auditor exigen) antes de disparar — si no, un
    // auditor puro recibe 0 filas en la primera carga y queda cacheado vacío.
    enabled: !!user && !!currentRole && auditorScope.isReady,
  });
}

/**
 * Extract ROI summaries from portfolio projects that have roi_data
 */
export function getROISummaries(projects: PortfolioProject[]): PortfolioROISummary[] {
  return projects
    .filter(p => p.roiData && p.roiData.costs && p.roiData.benefits)
    .map(p => {
      const costs = p.roiData!.costs;
      const benefits = p.roiData!.benefits;
      const totalCosts = costs.subscriptions + costs.tokenConsumption + costs.personnel + costs.training;
      const totalBenefits = benefits.reduce((sum, b) => sum + (b.type === 'not_defined' ? 0 : b.amount), 0);
      return {
        projectId: p.id,
        projectName: p.name,
        company: p.company || 'Sin empresa',
        costs,
        benefits,
        totalCosts,
        totalBenefits,
        roi: calculateROI(totalBenefits, totalCosts),
        bcr: calculateBCR(totalBenefits, totalCosts),
        paybackMonths: calculatePaybackMonths(totalBenefits, totalCosts),
      };
    });
}
